// panels/pvc/PvcReport.js —— 光储充 · 3.4 统计报表（需求文档 p14–p20）
// 3.4.1 收益统计（系统收益 / 光伏收益参考 / 储能收益参考）
// 3.4.2 电量统计（堆叠市电/负载/光伏 + 储能充放电，图表/表格 + 总/分切换）
// 3.4.3 电表报表（电表电量·本月 / 电表止度·本月）
// 3.4.4 自定义曲线（最多 10 项 / 模板 / 时段·实时查询 / 导出采样频率；全量单体导出最多 12h）
// 3.4.5 导出列表
// 对齐文档：3.4 仅上述 5 个页签，文档未要求「电池簇曲线」「单体电池」独立页签，已移除。
import React, { useMemo, useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import { Table, Select, DatePicker, Button, Radio, Segmented, Modal, Checkbox, Tag, Space, message,
} from 'antd';
import { DownloadOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { getDevices, getExportList } from '../../../../config/stationData';
import {
  getSevenDayEnergy, getSevenDayIncome, sumField, getMeterEnergyMonth, getMeterReadingMonth,
  METER_RATIO, CUSTOM_METRICS, getRealtimeCurve,
} from './data';
import './pvc.css';

const AXIS = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const GRID = 'rgba(255,255,255,0.08)';
const DASH = '5 4';
const TARIFF_SPLIT = { sharp: 0.12, peak: 0.30, flat: 0.37, valley: 0.21 };

const Kpi = ({ label, value, unit, foot, accent }) => (
  <div className="kpi-card">
    <div className="kpi-label">{label}</div>
    <div className="kpi-value" style={accent ? { color: accent } : undefined}>{value}<span className="unit">{unit}</span></div>
    {foot ? <div className="kpi-foot">{foot}</div> : null}
  </div>
);

const PvcReport = ({ station }) => {
  const dev = getDevices(station.id);

  // ---------- 3.4.1 收益统计 ----------
  const income7 = getSevenDayIncome(station.id);
  const incomeSum = {
    system: sumField(income7, 'system'),
    pv: sumField(income7, 'pv'),
    storage: sumField(income7, 'storage'),
  };

  // ---------- 3.4.2 电量统计 ----------
  const energy7 = getSevenDayEnergy(station.id);
  const energySum = {
    grid: sumField(energy7, 'grid'), load: sumField(energy7, 'load'), pv: sumField(energy7, 'pv'),
    charge: sumField(energy7, 'charge'), discharge: sumField(energy7, 'discharge'),
  };
  const [energyView, setEnergyView] = useState('chart');
  const [energyMode, setEnergyMode] = useState('total');
  const energyTotalCols = [
    { title: '日期', dataIndex: 'day' },
    { title: '市电电量 (kWh)', dataIndex: 'grid' },
    { title: '负载电量 (kWh)', dataIndex: 'load' },
    { title: '光伏电量 (kWh)', dataIndex: 'pv' },
    { title: '充电量 (kWh)', dataIndex: 'charge' },
    { title: '放电量 (kWh)', dataIndex: 'discharge' },
  ];
  const splitRows = energy7.map((r) => ({
    key: r.day, day: r.day,
    gridS: Math.round(r.grid * TARIFF_SPLIT.sharp), gridP: Math.round(r.grid * TARIFF_SPLIT.peak),
    gridF: Math.round(r.grid * TARIFF_SPLIT.flat), gridV: Math.round(r.grid * TARIFF_SPLIT.valley),
    loadS: Math.round(r.load * TARIFF_SPLIT.sharp), loadP: Math.round(r.load * TARIFF_SPLIT.peak),
    loadF: Math.round(r.load * TARIFF_SPLIT.flat), loadV: Math.round(r.load * TARIFF_SPLIT.valley),
    pvS: Math.round(r.pv * TARIFF_SPLIT.sharp), pvP: Math.round(r.pv * TARIFF_SPLIT.peak),
    pvF: Math.round(r.pv * TARIFF_SPLIT.flat), pvV: Math.round(r.pv * TARIFF_SPLIT.valley),
  }));
  const splitCols = [
    { title: '日期', dataIndex: 'day', fixed: 'left', width: 90 },
    { title: '市电·尖', dataIndex: 'gridS' }, { title: '市电·峰', dataIndex: 'gridP' },
    { title: '市电·平', dataIndex: 'gridF' }, { title: '市电·谷', dataIndex: 'gridV' },
    { title: '负载·尖', dataIndex: 'loadS' }, { title: '负载·峰', dataIndex: 'loadP' },
    { title: '负载·平', dataIndex: 'loadF' }, { title: '负载·谷', dataIndex: 'loadV' },
    { title: '光伏·尖', dataIndex: 'pvS' }, { title: '光伏·峰', dataIndex: 'pvP' },
    { title: '光伏·平', dataIndex: 'pvF' }, { title: '光伏·谷', dataIndex: 'pvV' },
  ];
  const sumRow = (
    <Table.Summary.Row className="pvc-summary-row">
      <Table.Summary.Cell index={0}>总计</Table.Summary.Cell>
      <Table.Summary.Cell index={1}>{energySum.grid}</Table.Summary.Cell>
      <Table.Summary.Cell index={2}>{energySum.load}</Table.Summary.Cell>
      <Table.Summary.Cell index={3}>{energySum.pv}</Table.Summary.Cell>
      <Table.Summary.Cell index={4}>{energySum.charge}</Table.Summary.Cell>
      <Table.Summary.Cell index={5}>{energySum.discharge}</Table.Summary.Cell>
    </Table.Summary.Row>
  );

  // ---------- 3.4.3 电表报表（默认本月 · 差异 #6）----------
  const meterOptions = dev.meters.map((m) => ({ value: m.id, label: m.name }));
  const [energyMeter, setEnergyMeter] = useState('M-01');
  const [readingMeter, setReadingMeter] = useState('M-01');
  const meterEnergyRows = getMeterEnergyMonth(energyMeter);
  const meterReadingRows = getMeterReadingMonth(readingMeter);
  const meterEnergyCols = [
    { title: '日期', dataIndex: 'day', fixed: 'left', width: 90 },
    { title: '累计正向(总)', dataIndex: 'fp' },
    { title: '尖', dataIndex: 'sharp' }, { title: '峰', dataIndex: 'peak' },
    { title: '平', dataIndex: 'flat' }, { title: '谷', dataIndex: 'valley' },
    { title: '反向有功', dataIndex: 'reverse' }, { title: '无功电量', dataIndex: 'reactive' },
  ];
  const meterEnergySum = (
    <Table.Summary.Row className="pvc-summary-row">
      {['总计', 'fp', 'sharp', 'peak', 'flat', 'valley', 'reverse', 'reactive'].map((k, i) => (
        <Table.Summary.Cell index={i} key={k}>
          {k === '总计' ? '总计' : sumField(meterEnergyRows, k).toLocaleString()}
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  // ---------- 3.4.4 自定义曲线（差异 #12）----------
  const baseCurve = useMemo(() => getRealtimeCurve(station.id), [station.id]);
  const [selected, setSelected] = useState(['grid', 'load', 'storage']); // 最多 10 项
  const [pickerOpen, setPickerOpen] = useState(false);
  const [metricDraft, setMetricDraft] = useState(selected);
  const [queryMode, setQueryMode] = useState('range');
  const [sampleRate, setSampleRate] = useState('15min');
  const [exportOpen, setExportOpen] = useState(false);
  const [template, setTemplate] = useState('默认模板');

  const metricMap = Object.fromEntries(CUSTOM_METRICS.map((m) => [m.key, m]));
  const curveData = useMemo(() => baseCurve.map((d) => {
    const row = { clock: d.clock };
    selected.forEach((k) => {
      if (k === 'cellVolume') row[k] = +(3.331 + Math.sin(d.soc / 6) * 0.01).toFixed(3);
      else if (k === 'cellTemp') row[k] = +(27.6 + Math.cos(d.soc / 5) * 0.8).toFixed(1);
      else row[k] = d[k];
    });
    return row;
  }), [baseCurve, selected]);

  const hasSingle = selected.some((k) => metricMap[k] && metricMap[k].single);

  // ---------- 3.4.5 导出列表 ----------
  const [exports, setExports] = useState(() => getExportList());

  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return (
    <>
    <ScrollTabs
      items={[
        {
          key: 'income',
          label: '收益统计',
          children: (
            <>
              <div className="dev-seg-bar">
                <DatePicker.RangePicker style={{ width: 300 }} />
                <Segmented options={[{ value: '7d', label: '近 7 天' }, { value: 'month', label: '当月' }, { value: 'year', label: '当年' }]} defaultValue="7d" />
                <Button icon={<DownloadOutlined />}>导出</Button>
              </div>
              <div className="kpi-grid">
                <Kpi label="系统收益" value={incomeSum.system.toLocaleString()} unit="元" accent="#52e8b0" foot="负载表电量×电网电价 − 电网总表电量×电网电价" />
                <Kpi label="光伏收益参考" value={incomeSum.pv.toLocaleString()} unit="元" accent="#f4c542" foot="光伏发电量×光伏发电电价" />
                <Kpi label="储能收益参考" value={incomeSum.storage.toLocaleString()} unit="元" accent="#4dd8ff" foot="储能放电量×电网电价 − 储能充电量×电网电价" />
              </div>
              <div className="panel">
                <div className="panel-title">收益曲线<small>系统收益 / 光伏收益参考 / 储能收益参考 · 元</small></div>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={income7} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis dataKey="day" tick={AXIS} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TT} />
                    <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} iconSize={8} />
                    <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
                    <Bar dataKey="system" name="系统收益" fill="#52e8b0" barSize={20} radius={[4, 4, 0, 0]} />
                    <Line type="linear" dataKey="pv" name="光伏收益参考" stroke="#f4c542" strokeWidth={2} />
                    <Line type="linear" dataKey="storage" name="储能收益参考" stroke="#4dd8ff" strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          ),
        },
        {
          key: 'energy',
          label: '电量统计',
          children: (
            <>
              <div className="dev-seg-bar">
                <Segmented value={energyView} onChange={setEnergyView} options={[{ value: 'chart', label: '图表' }, { value: 'table', label: '表格' }]} />
                <Segmented value={energyMode} onChange={setEnergyMode} options={[{ value: 'total', label: '总电量' }, { value: 'split', label: '分时电量' }]} />
              </div>
              {energyView === 'chart' ? (
                <div className="grid-2-even">
                  <div className="panel">
                    <div className="panel-title">每日电量（市电/负载/光伏）<small>顶部总计 · 市电 {energySum.grid.toLocaleString()} / 负载 {energySum.load.toLocaleString()} / 光伏 {energySum.pv.toLocaleString()} kWh</small></div>
                    <ResponsiveContainer width="100%" height={260}>
                      <ComposedChart data={energy7} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                        <XAxis dataKey="day" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={TT} />
                        <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} iconSize={8} />
                        <Bar dataKey="grid" name="市电电量" stackId="e" fill="#4dd8ff" barSize={22} />
                        <Bar dataKey="load" name="负载电量" stackId="e" fill="#ff5a7f" barSize={22} />
                        <Bar dataKey="pv" name="光伏电量" stackId="e" fill="#f4c542" barSize={22} radius={[4, 4, 0, 0]} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="panel">
                    <div className="panel-title">储能电量（充/放）<small>顶部总计 · 充电 {energySum.charge.toLocaleString()} / 放电 {energySum.discharge.toLocaleString()} kWh</small></div>
                    <ResponsiveContainer width="100%" height={260}>
                      <ComposedChart data={energy7} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                        <XAxis dataKey="day" tick={AXIS} axisLine={false} tickLine={false} />
                        <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={TT} />
                        <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} iconSize={8} />
                        <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
                        <Bar dataKey="charge" name="充电量" fill="#00d4ff" barSize={16} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="discharge" name="放电量" fill="#52e8b0" barSize={16} radius={[4, 4, 0, 0]} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : null}
              {energyView === 'table' ? (
                <div className="panel">
                  <div className="panel-title">电量数据表<small>{energyMode === 'total' ? '一天展示 5 种电量（市电/负载/光伏/充/放）' : '分时电量（尖/峰/平/谷）· 可横向滚动'}</small></div>
                  {energyMode === 'total' ? (
                    <Table size="small" rowKey="day" columns={energyTotalCols} dataSource={energy7} pagination={false} summary={() => sumRow} />
                  ) : (
                    <Table size="small" rowKey="day" columns={splitCols} dataSource={splitRows} pagination={false} scroll={{ x: 1100 }} />
                  )}
                </div>
              ) : null}
            </>
          ),
        },
        {
          key: 'meter',
          label: '电表报表',
          children: (
            <>
              <div className="panel">
                <div className="panel-title">
                  <span>电表电量<small style={{ marginLeft: 8 }}>默认查询本月（不含今日）：2026-10-01 ~ 2026-10-16</small></span>
                  <Space>
                    <Select size="small" value={energyMeter} onChange={setEnergyMeter} style={{ width: 170 }} options={meterOptions} />
                    <DatePicker.RangePicker size="small" style={{ width: 240 }} />
                    <Button size="small" icon={<DownloadOutlined />}>导出</Button>
                  </Space>
                </div>
                <Table size="small" rowKey="key" columns={meterEnergyCols} dataSource={meterEnergyRows} pagination={false} scroll={{ x: 900 }} summary={() => meterEnergySum} />
              </div>
              <div className="panel">
                <div className="panel-title">
                  <span>电表止度<small style={{ marginLeft: 8 }}>默认查询本月：2026-10-01 ~ 2026-10-16 · 展示标注日期 00:00 止度及综合倍率</small></span>
                  <Space>
                    <Select size="small" value={readingMeter} onChange={setReadingMeter} style={{ width: 170 }} options={meterOptions} />
                    <DatePicker.RangePicker size="small" style={{ width: 240 }} />
                  </Space>
                </div>
                <Table
                  size="small"
                  rowKey="key"
                  dataSource={meterReadingRows}
                  pagination={false}
                  columns={[
                    { title: '日期（00:00）', dataIndex: 'day' },
                    { title: '电表止度 (kWh)', dataIndex: 'reading' },
                    { title: '综合倍率', dataIndex: 'ratio', render: (v, r) => v || METER_RATIO[r.key] || 1 },
                  ]}
                />
              </div>
            </>
          ),
        },
        {
          key: 'custom',
          label: '自定义曲线',
          children: (
            <>
              <div className="panel">
                <div className="panel-title">
                  <span>已选数据<small style={{ marginLeft: 8 }}>最多 10 项</small></span>
                  <Space>
                    <Button size="small" icon={<PlusOutlined />} onClick={() => { setMetricDraft(selected); setPickerOpen(true); }}>添加数据</Button>
                    <Select size="small" value={template} onChange={setTemplate} style={{ width: 140 }}
                      options={[{ value: '默认模板', label: '默认模板' }, { value: '光储充运行', label: '光储充运行' }, { value: '单体全量', label: '单体全量' }]} />
                    <Button size="small" icon={<SaveOutlined />}>保存模板</Button>
                  </Space>
                </div>
                <Space wrap>
                  {selected.map((k) => (
                    <Tag key={k} color="cyan" closable onClose={() => setSelected((p) => p.filter((x) => x !== k))}>{metricMap[k] ? metricMap[k].label : k}</Tag>
                  ))}
                </Space>
              </div>
              <div className="panel">
                <div className="panel-title">
                  <span>自定义曲线查询<small style={{ marginLeft: 8 }}>查询范围：时段查询 / 实时查询</small></span>
                  <Space>
                    <Radio.Group value={queryMode} onChange={(e) => setQueryMode(e.target.value)}>
                      <Radio value="range">时段查询</Radio>
                      <Radio value="real">实时查询</Radio>
                    </Radio.Group>
                    <DatePicker.RangePicker showTime size="small" disabled={queryMode === 'real'} />
                    <Button size="small">查询</Button>
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => setExportOpen(true)}>导出</Button>
                  </Space>
                </div>
                {hasSingle ? (
                  <Tag color="orange" style={{ marginBottom: 12 }}>已选择「全量单体」数据：导出最多支持 12h</Tag>
                ) : null}
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={curveData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                    <XAxis dataKey="clock" interval={11} tick={AXIS} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TT} />
                    <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} iconSize={8} />
                    <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
                    {selected.map((k) => (
                      <Line key={k} type="linear" dataKey={k} name={metricMap[k] ? metricMap[k].label : k}
                        stroke={metricMap[k] ? metricMap[k].color : '#fff'} strokeWidth={2} dot={false}
                        strokeDasharray={(metricMap[k] && metricMap[k].single) ? DASH : undefined} />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          ),
        },
        {
          key: 'exports',
          label: '导出列表',
          children: (
            <div className="panel">
              <div className="panel-title">
                <span>导出列表<small style={{ marginLeft: 8 }}>最近导出任务</small></span>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => setExports((p) => p.map((e) => e.state === '处理中' ? { ...e, state: '已完成', size: `${(Math.random() * 12).toFixed(1)} KB` } : e))}>刷新</Button>
              </div>
              <Table size="small" rowKey="key" pagination={false} dataSource={exports}
                columns={[
                  { title: '文件名', dataIndex: 'name' },
                  { title: '类型', dataIndex: 'type' },
                  { title: '生成时间', dataIndex: 'time' },
                  { title: '状态', dataIndex: 'state', render: (v) => <Tag color={v === '已完成' ? 'green' : 'orange'}>{v}</Tag> },
                  { title: '大小', dataIndex: 'size' },
                  { title: '操作', render: (_, r) => r.state === '已完成'
                    ? <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => message.success('下载已开始（演示）')}>下载</Button>
                    : <span style={{ color: 'rgba(255,255,255,0.35)' }}>—</span> },
                ]} />
            </div>
          ),
        },
      ]}
    />
      <Modal
        title="添加数据（最多 10 项）"
        open={pickerOpen}
        onOk={() => { setSelected(metricDraft.slice(0, 10)); setPickerOpen(false); }}
        onCancel={() => setPickerOpen(false)}
        width={560}
        destroyOnHidden
      >
        <Checkbox.Group value={metricDraft} onChange={(v) => setMetricDraft(v)} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {CUSTOM_METRICS.map((m) => (
            <Checkbox key={m.key} value={m.key}>
              {m.label}{m.single ? <Tag color="orange" style={{ marginLeft: 6 }}>全量单体</Tag> : null}
            </Checkbox>
          ))}
        </Checkbox.Group>
        <div className="dev-sub">已选 {metricDraft.length} / 10 项</div>
      </Modal>

      <Modal
        title="导出 · 选择采样频率"
        open={exportOpen}
        onOk={() => {
          setExports([{ key: Date.now(), name: `自定义曲线_${sampleRate}.xlsx`, type: `自定义曲线（${selected.length} 测点）`, time: '刚刚', state: '处理中', size: '-' }, ...exports]);
          setExportOpen(false);
          message.success('导出任务已创建（异步），可在「导出列表」查询');
        }}
        onCancel={() => setExportOpen(false)}
        destroyOnHidden
      >
        <div className="dev-seg-bar">
          <span className="dev-kv-k" style={{ marginRight: 8 }}>采样频率</span>
          <Select value={sampleRate} onChange={setSampleRate} style={{ width: 180 }}
            options={[{ value: '1min', label: '1 分钟' }, { value: '5min', label: '5 分钟' }, { value: '15min', label: '15 分钟' }, { value: '1h', label: '1 小时' }]} />
        </div>
        {hasSingle ? <div className="pvc-tip-warn">含「全量单体」数据，导出时间范围最多 12h</div> : null}
      </Modal>
    </>
  );
};

export default PvcReport;
